import { lazy, Suspense, type ReactNode } from 'react'
import {
  hasLegacyUiBlock,
  readCanonicalUiBlock,
  uiBlocksToMarkdown,
  uiNodeToMarkdown,
  withoutLegacyUiBlocks,
} from '@hub/contracts'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import { useUiBlockHost } from './ui-block-host'

/**
 * The only way `message-markdown.tsx` reaches blocks: nothing of zod, the
 * components or the table code loads until a block or (flag on) a table is on
 * screen. Three chunks: stored blocks; old blocks; and OpenUI's parser, which the
 * first one imports only while a reply streams. A chunk that fails to load (a
 * deploy replaced it) must not take the message down, and history must not
 * degrade: the stored text is read right here and shown as its table, which is
 * why the canonical reader is in the first load.
 */

function QuietLine() {
  const { t } = useTranslation('chat')
  return <p className="text-muted-foreground text-xs italic">{t('uiBlocks.couldNotRender')}</p>
}

function UiBlockUnavailable({ source }: { source: string }) {
  const { renderMarkdown } = useUiBlockHost()
  const tree = readCanonicalUiBlock(source)
  const markdown = tree ? uiNodeToMarkdown(tree) : ''
  return markdown ? renderMarkdown(markdown) : <QuietLine />
}

const LazyUiBlock = lazy(() =>
  import('./ui-block').then(
    (module) => ({ default: module.UiBlock }),
    () => ({ default: UiBlockUnavailable })
  )
)

const LazyLegacyBlock = lazy(() =>
  import('./legacy-block').then(
    (module) => ({ default: module.LegacyUiBlock }),
    () => ({ default: QuietLine })
  )
)

const LazyMarkdownTable = lazy(() =>
  import('./markdown-table').then(
    (module) => ({ default: module.MarkdownTable }),
    () => ({ default: ({ fallback }: { fallback: ReactNode }) => <>{fallback}</> })
  )
)

/**
 * The markdown a message copies as. Synchronous unless the content holds an old
 * block: a clipboard write after an awaited import is what Safari refuses. If the
 * legacy chunk is gone the old blocks are left out, never their syntax.
 */
export function messageCopyText(content: string): string | Promise<string> {
  if (!hasLegacyUiBlock(content)) return uiBlocksToMarkdown(content)
  return import('@hub/contracts/ui-block-legacy').then(
    ({ convertLegacyUiBlocksText }) => uiBlocksToMarkdown(convertLegacyUiBlocksText(content).text),
    () => uiBlocksToMarkdown(withoutLegacyUiBlocks(content))
  )
}

const blockFallback = <Skeleton className="my-2 h-24 w-full rounded-lg" />

export function UiBlock({ source }: { source: string }) {
  return (
    <Suspense fallback={blockFallback}>
      <LazyUiBlock source={source} />
    </Suspense>
  )
}

export function LegacyUiBlock({ source }: { source: string }) {
  return (
    <Suspense fallback={blockFallback}>
      <LazyLegacyBlock source={source} />
    </Suspense>
  )
}

export function MarkdownTable({
  children,
  fallback,
}: {
  children: ReactNode
  fallback: ReactNode
}) {
  return (
    <Suspense fallback={fallback}>
      <LazyMarkdownTable fallback={fallback}>{children}</LazyMarkdownTable>
    </Suspense>
  )
}
